import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ThumbnailSidebar = ({ catalog, currentPage, onPageSelect, isOpen, onToggle }) => {
  const sortedPages = useMemo(() => {
    if (!catalog?.catalogPages) return [];
    return [...catalog?.catalogPages]?.sort((a, b) => (a?.pageNumber || 0) - (b?.pageNumber || 0));
  }, [catalog?.catalogPages]);

  const hotspotCounts = useMemo(() => {
    const counts = new Map();
    (catalog?.hotspots || [])?.forEach((hotspot) => {
      const pageNumber = hotspot?.pageNumber;
      if (!pageNumber) return;
      counts.set(pageNumber, (counts.get(pageNumber) || 0) + 1);
    });
    return counts;
  }, [catalog?.hotspots]);
  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
          onClick={onToggle}
        />
      )}
      {/* Sidebar */}
      <div 
        className={`
          fixed lg:relative top-0 right-0 h-full bg-card border-l border-border z-40
          transition-transform duration-300 ease-smooth
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          w-[280px] md:w-[320px] lg:w-[280px]
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Layers" size={20} className="text-accent" />
              <h3 className="text-base font-semibold text-foreground">Pages</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="lg:hidden w-8 h-8"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Catalog Info */}
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-1">
              {catalog?.title}
            </h4>
            <p className="text-xs text-muted-foreground">
              {catalog?.catalogPages?.length} pages • {catalog?.category}
            </p>
          </div>

          {/* Thumbnails */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-3">
              {sortedPages?.map((page) => (
                <button
                  key={page?.id}
                  onClick={() => {
                    onPageSelect(page?.pageNumber);
                    if (window.innerWidth < 1024) {
                      onToggle();
                    }
                  }}
                  className={`
                    group relative aspect-[3/4] rounded-lg overflow-hidden border-2 transition-all duration-250
                    ${currentPage === page?.pageNumber
                      ? 'border-accent shadow-elevation-md' 
                      : 'border-border hover:border-accent/50 hover:shadow-elevation-sm'
                    }
                  `}
                >
                  <Image
                    src={page?.thumbnailUrl || page?.imageUrl}
                    alt={page?.thumbnailAlt || page?.imageAlt || `Page ${page?.pageNumber}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Page Number Overlay */}
                  <div className={`
                    absolute inset-0 flex items-center justify-center
                    bg-gradient-to-t from-background/80 to-transparent
                    opacity-0 group-hover:opacity-100 transition-opacity duration-250
                    ${currentPage === page?.pageNumber ? 'opacity-100' : ''}
                  `}>
                    <span className="text-sm font-semibold text-foreground bg-card/90 px-3 py-1 rounded-full">
                      {page?.pageNumber}
                    </span>
                  </div>

                  {/* Hotspot Indicator */}
                  {hotspotCounts?.get(page?.pageNumber) > 0 && (
                    <div className="absolute top-2 right-2 bg-accent/90 text-accent-foreground rounded-full px-2 py-0.5 text-xs font-medium">
                      No. de hotspost: {hotspotCounts?.get(page?.pageNumber)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Toggle Button (Mobile) */}
      <Button
        variant="secondary"
        size="icon"
        onClick={onToggle}
        className="fixed bottom-20 right-4 z-30 lg:hidden w-12 h-12 rounded-full shadow-elevation-lg"
      >
        <Icon name={isOpen ? 'X' : 'Layers'} size={24} />
      </Button>
    </>
  );
};

export default ThumbnailSidebar;